<template>
  <div :class="$style.container">
    <!-- ヘッダー情報 -->
    <section :class="$style.headerSection">
      <div :class="$style.statusArea">
        <StateChip
          :text="statusString(props.calendar.status)"
          :variation="statusVariation(props.calendar.status)"
        />
      </div>
      <div :class="$style.headerContent">
        <h2 :class="$style.publicTitle">{{ props.calendar.publicTitle }}</h2>
        <p v-if="props.calendar.privateTitle" :class="$style.privateTitle">
          {{ props.calendar.privateTitle }}
        </p>
        <p v-if="props.calendar.description" :class="$style.description">
          {{ props.calendar.description }}
        </p>
      </div>
    </section>

    <!-- 公開設定セクション -->
    <section :class="$style.section">
      <div :class="$style.sectionHeader">
        <IconExternalLink :class="$style.sectionIcon" />
        <h3 :class="$style.sectionTitle">公開設定</h3>
      </div>
      <div :class="$style.sectionContent">
        <div :class="$style.urlField">
          <label :class="$style.fieldLabel">
            日程調整用公開URL
            <span :class="$style.fieldNote"
              >（オカネコ登録ユーザーのみ利用可能）</span
            >
          </label>
          <div :class="$style.urlContainer">
            <div :class="$style.urlText">{{ calendarURL }}</div>
            <FlatButton
              :class="$style.copyButton"
              @click="copyUrl"
              size="small"
              aria-label="公開URLをコピー"
            >
              <template #icon>
                <IconClipboard />
              </template>
              コピー
            </FlatButton>
          </div>
        </div>
      </div>
    </section>

    <!-- 予約枠設定セクション -->
    <section :class="$style.section">
      <div :class="$style.sectionHeader">
        <IconTimer :class="$style.sectionIcon" />
        <h3 :class="$style.sectionTitle">予約枠設定</h3>
      </div>
      <div :class="$style.sectionContent">
        <div :class="$style.settingsGrid">
          <div :class="$style.settingsGroup">
            <h4 :class="$style.groupTitle">時間設定</h4>
            <div :class="$style.settingsList">
              <div :class="$style.settingItem">
                <span :class="$style.settingLabel">1枠の所要時間:</span>
                <span :class="$style.settingValue"
                  >{{ props.calendar.durationMinutes }}分</span
                >
              </div>
              <div :class="$style.settingItem">
                <span :class="$style.settingLabel">枠の表示間隔:</span>
                <span :class="$style.settingValue"
                  >{{ props.calendar.offsetMinutes }}分</span
                >
              </div>
            </div>
          </div>
          <div :class="$style.settingsGroup">
            <h4 :class="$style.groupTitle">表示設定</h4>
            <div :class="$style.settingsList">
              <div :class="$style.settingItem">
                <span :class="$style.settingLabel">枠表示開始タイミング:</span>
                <span :class="$style.settingValue"
                  >{{ props.calendar.slotGenerationDelayHours }}時間後から</span
                >
              </div>
              <div :class="$style.settingItem">
                <span :class="$style.settingLabel">枠表示期間:</span>
                <span :class="$style.settingValue"
                  >{{ props.calendar.slotGenerationPeriodDays }}日間分</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 担当者設定セクション -->
    <section :class="$style.section">
      <div :class="$style.sectionHeader">
        <IconPerson :class="$style.sectionIcon" />
        <h3 :class="$style.sectionTitle">担当者設定</h3>
      </div>
      <div :class="$style.sectionContent">
        <div :class="$style.settingItem">
          <span :class="$style.settingLabel">担当者選出方法:</span>
          <span :class="$style.settingValue">
            {{
              getStaffSelectionStrategyLabel(
                props.calendar.staffSelectionStrategy
              )
            }}
          </span>
        </div>
        <div :class="$style.staffSection">
          <h4 :class="$style.staffTitle">担当者候補</h4>
          <div
            v-if="assignedStaffs.length > 0 && linkedStaffGUIDs"
            :class="$style.staffContainer"
          >
            <!-- 優先度順の場合 -->
            <div
              v-if="props.calendar.staffSelectionStrategy === 'PRIORITY'"
              :class="$style.priorityList"
            >
              <div
                v-for="staff in assignedStaffs"
                :key="staff.guid"
                :class="$style.staffItem"
              >
                <span :class="$style.staffItem__priority"
                  >{{ staff.priority }}.</span
                >
                <span
                  :class="[
                    $style.staffItem__name,
                    {
                      [$style['staffItem__name--linked']]:
                        linkedStaffGUIDs.includes(staff.guid),
                      [$style['staffItem__name--unlinked']]:
                        !linkedStaffGUIDs.includes(staff.guid),
                    },
                  ]"
                >
                  {{ staff.displayName }}
                  <template v-if="!linkedStaffGUIDs.includes(staff.guid)"
                    >(アカウント再連携が必要)</template
                  >
                </span>
              </div>
            </div>
            <!-- ランダムの場合 -->
            <div v-else :class="$style.staffBadges">
              <div
                v-for="staff in assignedStaffs"
                :key="staff.guid"
                :class="[
                  $style.staffBadge,
                  {
                    [$style['staffBadge__linked']]: linkedStaffGUIDs.includes(
                      staff.guid
                    ),
                    [$style['staffBadge__unlinked']]:
                      !linkedStaffGUIDs.includes(staff.guid),
                  },
                ]"
              >
                {{ staff.displayName }}
                <template v-if="!linkedStaffGUIDs.includes(staff.guid)"
                  >(アカウント再連携が必要)</template
                >
              </div>
            </div>
          </div>
          <p v-else :class="$style.noStaff">担当者が設定されていません</p>
        </div>
      </div>
    </section>

    <!-- 利用可能な提示日時設定セクション -->
    <section :class="$style.section">
      <div :class="$style.sectionHeader">
        <IconCalendar :class="$style.sectionIcon" />
        <h3 :class="$style.sectionTitle">曜日と時間帯設定</h3>
      </div>
      <div :class="$style.sectionContent">
        <div :class="$style.scheduleGrid">
          <div
            v-for="day in weekdayNames.keys()"
            :key="day"
            :class="$style.daySchedule"
          >
            <div :class="$style.dayName">
              {{ weekdayNames.get(day) }}
            </div>
            <div :class="$style.dayTimes">
              <template v-if="getDayAvailabilities(day).length > 0">
                <div
                  v-for="availability in getDayAvailabilities(day).slice(
                    0,
                    displayLimitForDayAvailability
                  )"
                  :key="`${availability.dayOfWeek}-${formatTime(availability.from)}-${formatTime(availability.until)}`"
                  :class="$style.timeSlot"
                >
                  {{ formatTime(availability.from) }} -
                  {{ formatTime(availability.until) }}
                </div>
                <div
                  v-if="
                    getDayAvailabilities(day).length >
                    displayLimitForDayAvailability
                  "
                  :class="$style.moreSlots"
                >
                  +{{
                    getDayAvailabilities(day).length -
                    displayLimitForDayAvailability
                  }}件
                </div>
              </template>
              <div v-else :class="$style.noSchedule">未設定</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 質問事項セクション -->
    <section :class="$style.section">
      <div :class="$style.sectionHeader">
        <IconClipboard :class="$style.sectionIcon" />
        <h3 :class="$style.sectionTitle">予約時の質問事項</h3>
      </div>
      <div
        v-if="questions.length !== 0"
        :class="[$style.sectionContent, $style.questionsList]"
      >
        <CalendarDetailQuestion
          v-for="(question, index) in questions"
          :key="index"
          :class="$style.questionsList__item"
          :question="question"
        />
      </div>
      <div v-else>未設定</div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import FlatButton from '@/components/atoms/FlatButton.vue'
import StateChip from '@/components/atoms/StateChip.vue'
import IconCalendar from '@/components/icons/IconCalendar.vue'
import IconClipboard from '@/components/icons/IconClipboard.vue'
import IconExternalLink from '@/components/icons/IconExternalLink.vue'
import IconPerson from '@/components/icons/IconPerson.vue'
import IconTimer from '@/components/icons/IconTimer.vue'
import { getShadowCalendarURL } from '@/config'
import { useAlert } from '@/libs/alertPortal'
import type { FormType } from './formSchema'
import {
  weekdayNames,
  statusString,
  statusVariation,
  formatTime,
  getStaffSelectionStrategyLabel,
} from './calendar.ts'
import type { DayOfWeek } from '@/types/schema.generated.ts'
import { useCalendar } from '@/hooks/useCalendar.ts'
import CalendarDetailQuestion from './questions/CalendarDetailQuestion/CalendarDetailQuestion.vue'

const props = defineProps<{
  calendarGUID: string
  calendar: FormType
}>()

const { linkedStaffs } = useCalendar()
const linkedStaffGUIDs = computed(() => {
  if (!linkedStaffs.value) {
    return undefined
  }
  return linkedStaffs.value.map(staff => staff.guid)
})

const assignedStaffs = computed(() => {
  if (
    !props.calendar.assignedStaffs ||
    props.calendar.assignedStaffs.length === 0
  ) {
    return []
  }

  // For PRIORITY, sort by priority. Otherwise, the order doesn't strictly matter for display.
  if (props.calendar.staffSelectionStrategy === 'PRIORITY') {
    // eslint-disable-next-line fp/no-mutating-methods
    return [...props.calendar.assignedStaffs].sort(
      (a, b) => (a.priority ?? Infinity) - (b.priority ?? Infinity)
    )
  }
  return props.calendar.assignedStaffs
})

// Get availabilities for a specific day
const getDayAvailabilities = (day: DayOfWeek) => {
  // Filter availabilities for the specified day and sort them by from time
  return props.calendar.dailyAvailabilities.filter(
    availability => availability.dayOfWeek === day
  )
}

const displayLimitForDayAvailability = 3

const calendarURL = computed(() => getShadowCalendarURL(props.calendarGUID))

const alert = useAlert()

const copyUrl = async () => {
  await navigator.clipboard.writeText(calendarURL.value)

  alert.success('カレンダーの公開URLをコピーしました')
}

const questions = computed(() => {
  return props.calendar.questions
})
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

// ヘッダーセクション
.headerSection {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.statusArea {
  padding-top: 4px;
}

.headerContent {
  flex: 1;
}

.publicTitle {
  font-size: 24px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis;
  margin: 0;
  line-height: 1.3;
}

.privateTitle {
  margin-top: 4px;
  font-size: 16px;
  color: $color-text-and-object-medium-emphasis;
  line-height: 1.3;
}

.description {
  margin-top: 8px;
  font-size: 16px;
  color: $color-text-and-object-medium-emphasis;
  line-height: 1.5;
}

// セクション共通スタイル
.section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sectionHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.sectionIcon {
  width: 20px;
  height: 20px;
  color: $color-text-and-object-accent-primary;
}

.sectionTitle {
  font-size: 18px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis;
  margin: 0;
}

.sectionContent {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// 公開設定セクション
.urlField {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fieldLabel {
  font-size: 14px;
  font-weight: bold;
  color: $color-text-and-object-medium-emphasis;
}

.fieldNote {
  font-weight: normal;
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;
}

.urlContainer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.urlText {
  flex: 1;
  padding: 12px 16px;
  background-color: $color-surface-secondary;
  border-radius: $border-radius-v2-base;
  font-size: 14px;
  color: $color-text-and-object-high-emphasis;
  word-break: break-all;
}

.copyButton {
  flex-shrink: 0;
}

// 予約枠設定セクション
.settingsGrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (width <= 768px) {
    grid-template-columns: 1fr;
  }
}

.settingsGroup {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.groupTitle {
  font-size: 16px;
  font-weight: bold;
  color: $color-text-and-object-medium-emphasis;
  margin: 0;
}

.settingsList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settingItem {
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 8px;
}

.settingLabel {
  font-size: 14px;
  flex-shrink: 0;
  color: $color-text-and-object-high-emphasis;
}

.settingValue {
  font-size: 14px;
  color: $color-text-and-object-high-emphasis;
}

// 担当者設定セクション
.staffSection {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.staffTitle {
  font-size: 16px;
  font-weight: bold;
  color: $color-text-and-object-medium-emphasis;
  margin: 0;
}

.staffContainer {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.priorityList {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.staffItem {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;

  &__priority {
    font-weight: bold;
    color: $color-text-and-object-accent-primary;
    min-width: 24px;
  }

  &__name {
    font-size: 14px;

    &--linked {
      color: $color-text-and-object-high-emphasis;
    }

    &--unlinked {
      color: $color-text-and-object-accent-red;
    }
  }
}

.staffBadges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.staffBadge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 14px;
  border-radius: $border-radius-v2-base;

  &__linked {
    background-color: $color-surface-secondary-opaque;
    color: $color-text-and-object-high-emphasis;
  }

  &__unlinked {
    background-color: $color-surface-accent-red;
    color: $color-text-and-object-high-emphasis-inverse;
  }
}

.noStaff {
  font-style: italic;
  color: $color-text-and-object-medium-emphasis;
  font-size: 14px;
  margin: 0;
}

// 営業時間設定セクション
.scheduleGrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.daySchedule {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dayName {
  font-size: 14px;
  font-weight: bold;
  color: $color-text-and-object-medium-emphasis;
  padding-bottom: 4px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.dayTimes {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 60px;
}

.timeSlot {
  font-size: 13px;
  color: $color-text-and-object-high-emphasis;
  padding: 2px 0;
}

.moreSlots {
  font-size: 12px;
  color: $color-text-and-object-medium-emphasis;
  font-style: italic;
  margin-top: 4px;
}

.noSchedule {
  font-size: 13px;
  color: $color-text-and-object-low-emphasis;
  font-style: italic;
}

.questionsList {
  display: flex;
  flex-flow: column wrap;
  gap: 0;

  &__item {
    &:not(:first-child) {
      padding-top: 16px;
    }

    &:not(:last-child) {
      padding-bottom: 16px;
      border-bottom: 1px solid $color-border-low-emphasis;
    }
  }
}
</style>
